import { useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Cake,
  Gift,
  PartyPopper,
  Phone,
  Plus,
  Send,
  Trash2,
  CalendarDays,
  MapPin,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAppState } from "@/context/AppState";
import { FelicitacionDialog } from "@/components/FelicitacionDialog";
import {
  mergeClients,
  daysUntilBirthday,
  formatBirthday,
  formatFecha,
  waLink,
  buildEventMessage,
} from "@/lib/agenda";

const emptyEvent = {
  title: "",
  kind: "curso",
  date: "",
  time: "",
  location: "",
  description: "",
  recipients_mode: "all",
  recipient_phones: [],
};

export default function Efemerides() {
  const { appointments, clients, events, addEvent, deleteEvent } = useAppState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);

  const merged = useMemo(() => mergeClients(clients, appointments), [clients, appointments]);
  const withPhone = merged.filter((c) => c.phone);
  const [felicitacion, setFelicitacion] = useState(null);

  const birthdays = useMemo(
    () =>
      merged
        .filter((c) => c.birthday)
        .map((c) => ({ ...c, days: daysUntilBirthday(c.birthday) }))
        .sort((a, b) => a.days - b.days),
    [merged]
  );

  const felicitar = (client, kind) => {
    if (!client.phone) {
      toast.error("Este cliente no tiene teléfono.");
      return;
    }
    setFelicitacion({ target: client, kind });
  };

  const togglePhone = (phone) => {
    setForm((f) => ({
      ...f,
      recipient_phones: f.recipient_phones.includes(phone)
        ? f.recipient_phones.filter((p) => p !== phone)
        : [...f.recipient_phones, phone],
    }));
  };

  const saveEvent = async () => {
    if (!form.title.trim() || !form.date) {
      toast.error("Pon al menos título y fecha.");
      return;
    }
    if (form.recipients_mode === "select" && form.recipient_phones.length === 0) {
      toast.error("Elige al menos un cliente.");
      return;
    }
    setSaving(true);
    try {
      await addEvent({
        ...form,
        title: form.title.trim(),
        recipient_phones: form.recipients_mode === "all" ? [] : form.recipient_phones,
      });
      toast.success("Evento guardado.");
      setForm(emptyEvent);
      setOpen(false);
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    } finally {
      setSaving(false);
    }
  };

  const eventRecipients = (ev) =>
    ev.recipients_mode === "all"
      ? withPhone
      : withPhone.filter((c) => (ev.recipient_phones || []).includes(c.phone));

  return (
    <div className="space-y-6 animate-fade-up" data-testid="efemerides-page">
      <div className="flex items-center gap-3">
        <span className="flex items-center justify-center h-11 w-11 rounded-2xl text-white" style={{ backgroundColor: "#2E2A8F" }}>
          <PartyPopper className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Efemérides</h1>
          <p className="text-sm text-slate-500 mt-0.5">Felicita cumpleaños y Navidades, y avisa de tus cursos y conferencias.</p>
        </div>
      </div>

      <Tabs defaultValue="cumpleanos" className="w-full">
        <TabsList className="rounded-full bg-slate-100 p-1" data-testid="efemerides-tabs">
          <TabsTrigger value="cumpleanos" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-cumpleanos">
            <Cake className="h-4 w-4" /> Cumpleaños
          </TabsTrigger>
          <TabsTrigger value="navidades" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-navidades">
            <Gift className="h-4 w-4" /> Navidades
          </TabsTrigger>
          <TabsTrigger value="eventos" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-eventos">
            <Sparkles className="h-4 w-4" /> Eventos
          </TabsTrigger>
        </TabsList>

        {/* Cumpleaños */}
        <TabsContent value="cumpleanos" className="mt-5">
          {birthdays.length === 0 ? (
            <p className="text-sm text-slate-400">
              Todavía no hay cumpleaños. Añade la fecha de nacimiento en la ficha del cliente.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="birthdays-list">
              {birthdays.map((c, i) => (
                <div
                  key={c.phone || c.name || i}
                  className={`bg-white rounded-2xl border p-5 shadow-[0_6px_24px_-12px_rgba(46,42,143,0.10)] transition-all ${
                    c.days === 0 ? "border-[#275FBE]" : "border-[#EAE5DF]"
                  }`}
                  data-testid={`birthday-card-${c.phone || c.name}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{c.name}</p>
                      <p className="text-xs text-slate-500 inline-flex items-center gap-1.5 mt-0.5">
                        <Cake className="h-3.5 w-3.5" /> {formatBirthday(c.birthday)}
                      </p>
                    </div>
                    {c.days === 0 ? (
                      <span className="text-xs font-semibold text-white px-2.5 py-1 rounded-full" style={{ backgroundColor: "#275FBE" }}>
                        Hoy
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">en {c.days} días</span>
                    )}
                  </div>
                  <Button
                    onClick={() => felicitar(c, "cumpleanos")}
                    className="w-full mt-4 rounded-full text-white gap-2"
                    style={{ backgroundColor: "#275FBE" }}
                    disabled={!c.phone}
                    data-testid={`felicitar-cumple-${c.phone || c.name}`}
                  >
                    <Send className="h-4 w-4" /> Felicitar por WhatsApp
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Navidades */}
        <TabsContent value="navidades" className="mt-5 space-y-4">
          <div className="bg-white rounded-2xl border border-[#EAE5DF] p-4 flex items-center gap-3">
            <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#F3EDE4] text-[#2E2A8F]">
              <Gift className="h-5 w-5" />
            </span>
            <p className="text-sm text-slate-600">
              Felicitación de Navidad para el <strong>25 de diciembre</strong>. La frase la prepara la IA y tú la envías por WhatsApp.
            </p>
          </div>
          {withPhone.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no tienes clientes con teléfono.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="navidad-list">
              {withPhone.map((c, i) => (
                <div
                  key={c.phone || c.name || i}
                  className="bg-white rounded-2xl border border-[#EAE5DF] p-5 shadow-[0_6px_24px_-12px_rgba(46,42,143,0.10)]"
                  data-testid={`navidad-card-${c.phone || c.name}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center h-9 w-9 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                      {c.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{c.name}</p>
                      <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {c.phone}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => felicitar(c, "navidad")}
                    variant="outline"
                    className="w-full mt-4 rounded-full gap-2 border-[#275FBE] text-[#275FBE] hover:bg-[#275FBE] hover:text-white"
                    data-testid={`felicitar-navidad-${c.phone || c.name}`}
                  >
                    <Send className="h-4 w-4" /> Felicitar la Navidad
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Eventos */}
        <TabsContent value="eventos" className="mt-5 space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => { setForm(emptyEvent); setOpen(true); }}
              className="rounded-full text-white gap-2"
              style={{ backgroundColor: "#275FBE" }}
              data-testid="add-event-button"
            >
              <Plus className="h-4 w-4" /> Crear evento
            </Button>
          </div>

          {events.length === 0 ? (
            <p className="text-sm text-slate-400">
              Todavía no tienes eventos. Crea el primero para avisar a tus clientes.
            </p>
          ) : (
            <div className="space-y-3" data-testid="events-list">
              {events.map((ev) => {
                const recipients = eventRecipients(ev);
                return (
                  <div
                    key={ev.id}
                    className="bg-white rounded-2xl border border-[#EAE5DF] p-5 shadow-[0_6px_24px_-12px_rgba(46,42,143,0.10)]"
                    data-testid={`event-card-${ev.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-[#2E2A8F] bg-[#F3EDE4] px-2 py-0.5 rounded-full">
                            {ev.kind === "conferencia" ? "Conferencia" : "Curso"}
                          </span>
                        </div>
                        <p className="font-medium text-slate-900 mt-1.5">{ev.title}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays className="h-3.5 w-3.5" /> {formatFecha(ev.date)}{ev.time ? ` · ${ev.time}` : ""}
                          </span>
                          {ev.location && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" /> {ev.location}
                            </span>
                          )}
                        </div>
                        {ev.description && <p className="text-sm text-slate-600 mt-2">{ev.description}</p>}
                        <p className="text-xs text-slate-400 mt-2">
                          {ev.recipients_mode === "all" ? "Para todos los clientes" : "Para clientes seleccionados"} · {recipients.length} destinatarios
                        </p>
                      </div>
                      <button
                        onClick={async () => { await deleteEvent(ev.id); toast.success("Evento borrado."); }}
                        className="text-slate-400 hover:text-red-500 transition-colors p-1 shrink-0"
                        title="Borrar evento"
                        data-testid={`delete-event-${ev.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {recipients.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-2">Avisar por WhatsApp:</p>
                        <div className="flex flex-wrap gap-2">
                          {recipients.map((c, i) => (
                            <a
                              key={c.phone || i}
                              href={waLink(c.phone, buildEventMessage(ev, c))}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-[#275FBE] text-[#275FBE] hover:bg-[#275FBE] hover:text-white transition-colors"
                              data-testid={`event-wa-${ev.id}-${c.phone || i}`}
                            >
                              <Send className="h-3 w-3" /> {c.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal crear evento */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px] rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="event-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900">Crear evento</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Un curso o conferencia para avisar a tus clientes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="e-title">Título</Label>
              <Input id="e-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Taller de gestión del estrés" data-testid="event-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="event-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="curso">Curso</SelectItem>
                    <SelectItem value="conferencia">Conferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="e-time">Hora</Label>
                <Input id="e-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-time" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="e-date">Fecha</Label>
                <Input id="e-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="e-location">Lugar</Label>
                <Input id="e-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Consulta, online..." data-testid="event-location" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="e-desc">Descripción</Label>
              <Textarea id="e-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="event-description" />
            </div>

            <div className="space-y-2">
              <Label>Destinatarios</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, recipients_mode: "all" })}
                  className={`flex-1 text-sm rounded-full py-2 border transition-colors ${form.recipients_mode === "all" ? "text-white border-transparent" : "text-slate-600 border-slate-200"}`}
                  style={form.recipients_mode === "all" ? { backgroundColor: "#275FBE" } : {}}
                  data-testid="recipients-all"
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, recipients_mode: "select" })}
                  className={`flex-1 text-sm rounded-full py-2 border transition-colors ${form.recipients_mode === "select" ? "text-white border-transparent" : "text-slate-600 border-slate-200"}`}
                  style={form.recipients_mode === "select" ? { backgroundColor: "#275FBE" } : {}}
                  data-testid="recipients-select"
                >
                  Seleccionar
                </button>
              </div>

              {form.recipients_mode === "select" && (
                <div className="max-h-44 overflow-y-auto rounded-2xl border border-slate-200 p-2 space-y-1" data-testid="recipients-picker">
                  {withPhone.length === 0 ? (
                    <p className="text-xs text-slate-400 p-2">No hay clientes con teléfono.</p>
                  ) : (
                    withPhone.map((c, i) => (
                      <label
                        key={c.phone || i}
                        className="flex items-center gap-3 px-2 py-1.5 rounded-xl hover:bg-slate-50 cursor-pointer"
                      >
                        <Checkbox
                          checked={form.recipient_phones.includes(c.phone)}
                          onCheckedChange={() => togglePhone(c.phone)}
                          data-testid={`recipient-${c.phone}`}
                        />
                        <span className="text-sm text-slate-700">{c.name}</span>
                        <span className="text-xs text-slate-400 ml-auto">{c.phone}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <Button
            className="w-full rounded-full text-white"
            style={{ backgroundColor: "#275FBE" }}
            onClick={saveEvent}
            disabled={saving}
            data-testid="save-event-button"
          >
            {saving ? "Guardando..." : "Guardar evento"}
          </Button>
        </DialogContent>
      </Dialog>

      <FelicitacionDialog
        open={!!felicitacion}
        target={felicitacion?.target}
        kind={felicitacion?.kind}
        onClose={() => setFelicitacion(null)}
      />
    </div>
  );
}
